import { observer }           from 'mobx-react-lite';
import Spin                  from '@widesk-ui/components/Spin';
import Tooltip               from '@widesk-ui/components/Tooltip';
import { CheckCircleOutlined } from '@ant-design/icons';
import { CloseCircleOutlined } from '@ant-design/icons';
import useDesignTokens       from '@/hooks/useDesignTokens';

/**
 * État d'enregistrement du panneau, affiché dans l'en-tête de la sidebar.
 *
 * Nécessaire depuis que ces écrans n'ont plus de bouton : sans bouton, plus rien ne dit que le geste
 * a été pris en compte, et une couleur qui change sous le curseur ne prouve pas qu'elle est partie.
 *
 * Réduit à son icône, le mot en infobulle : il partage sa ligne avec un titre que l'utilisateur
 * écrit lui-même, dans un panneau de 400px. « Enregistrement… » y prenait une centaine de pixels et
 * rognait d'autant le nom du point d'intérêt, qui est l'information principale de l'en-tête.
 */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const LABELS: Record<Exclude<SaveState, 'idle'>, string> = {
	saving: 'Enregistrement…',
	saved: 'Enregistré',
	error: `L'enregistrement a échoué`,
};

const SaveIndicator = observer(({ state }: { state: SaveState; }) => {
	const tokens = useDesignTokens();

	if (state === 'idle') return null;

	// Une coche et une croix plutôt que deux ronds de couleur : elles disent déjà "c'est fait" et
	// "ça a raté" sans qu'on ait à connaître le code couleur, pour la même largeur.
	//
	// 15px : sans mot à côté, l'icône porte seule le message et se lisait petite. Reste en dessous du
	// titre (16px, gras) qu'elle accompagne.
	const icon = state === 'saving'
		? <Spin size="small" />
		: state === 'error'
			? <CloseCircleOutlined style={{ fontSize: 15 }} />
			: <CheckCircleOutlined style={{ fontSize: 15 }} />;

	const color = state === 'saving'
		? tokens.text.muted
		: state === 'error'
			? tokens.color.error
			: tokens.color.success;

	// Un `span` et non un `View` : `Tooltip` (antd) déclenche l'affichage en clonant son enfant avec
	// des `onMouseEnter`/`onMouseLeave`, et `View` ne pose sur son `div` que les événements qu'il
	// connaît — ceux-là étaient donc perdus, et l'infobulle ne s'ouvrait jamais.
	return (
		<Tooltip title={LABELS[state]}>
			<span style={{ display: 'inline-flex', alignItems: 'center', color, flexShrink: 0 }}>
				{icon}
			</span>
		</Tooltip>
	);
});

export default SaveIndicator;
